/* eslint-disable indent */
import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { Dimension, IUploadImageDialog } from './interfaces';
import { Dialog } from '../Dialog';
import { Button } from '../Dialog/components/Button';
import { useTranslation } from 'react-i18next';
import { Dropzone } from './components/Dropzone';
import { Cropper, ReactCropperElement } from 'react-cropper';
import 'cropperjs/dist/cropper.css';
import {
  calculateAspectRatio,
  getDisplayMinCropSize,
  getNaturalCropDimensions,
  imageMeetsMinDimension,
  meetsMinDimension,
} from './helpers';
import { ImageValidationResult } from './interfaces';
import { Alert } from '@components/Alert';
import { Loader } from '@components/feed/Loader';
import { Box } from '@mui/material';

export const UploadImageDialog: React.FC<IUploadImageDialog> = ({
  title,
  subTitle,
  show,
  minDimension,
  maxDimension,
  aspectRatio,
  cropperContent,
  dropzoneContent,
  onClose,
  onCrop,
  validateImage,
  onImageLoad,
}): JSX.Element => {
  const { t } = useTranslation();
  const [filename, setFilename] = useState<string | null>(null);
  const [image, setImage] = useState<string | null>(null);
  const cropperRef = useRef<ReactCropperElement>(null);
  const [minCropSize, setMinCropSize] = useState<Dimension | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  const cropperAspectRatio = ((): number | undefined => {
    if (!aspectRatio) {
      return undefined;
    }

    if (aspectRatio === 'minDimension' && minDimension) {
      return calculateAspectRatio(minDimension);
    }

    if (aspectRatio === 'maxDimension' && maxDimension) {
      return calculateAspectRatio(maxDimension);
    }

    return aspectRatio as number;
  })();

  const syncMinCropSize = useCallback((): void => {
    const cropper = cropperRef.current?.cropper;

    if (!cropper) {
      return;
    }

    if (!minDimension) {
      const fallbackMinCropSize = {
        width: 100,
        height: 100,
      };

      setMinCropSize(fallbackMinCropSize);
      cropper.options.minCropBoxWidth = fallbackMinCropSize.width;
      cropper.options.minCropBoxHeight = fallbackMinCropSize.height;

      return;
    }

    const displayMinCropSize = getDisplayMinCropSize(cropper, minDimension);

    setMinCropSize(displayMinCropSize);
    cropper.options.minCropBoxWidth = displayMinCropSize.width;
    cropper.options.minCropBoxHeight = displayMinCropSize.height;

    if (cropper.ready) {
      cropper.initCropBox();

      if (cropper.cropped) {
        cropper.renderCropBox();
      }
    }
  }, [minDimension]);

  const reset = (): void => {
    setImage(null);
    setFilename(null);
    setMinCropSize(null);
    setIsProcessing(false);

    const cropper = cropperRef.current?.cropper;

    if (!cropper) {
      return;
    }

    cropper.reset();
  };

  const handleClose = (): void => {
    reset();
    setError(null);

    if (onClose) {
      onClose();
    }
  };

  const validateSelectedImage = (img: HTMLImageElement): ImageValidationResult => {
    if (validateImage) {
      return validateImage(img);
    }

    if (minDimension && !imageMeetsMinDimension(img, minDimension)) {
      return {
        valid: false,
        error: t('dialogs.uploadImage.errors.imageTooSmall'),
      };
    }

    return { valid: true };
  };

  const onImageSelect = (acceptedFiles: File[]): void => {
    const file = acceptedFiles[0];
    const reader = new FileReader();

    setError(null);

    reader.onload = () => {
      const dataUrl = reader.result as string;
      const img = new Image();

      img.onload = () => {
        const validation = validateSelectedImage(img);

        if (!validation.valid) {
          setError(
            validation.error || t('dialogs.uploadImage.errors.invalidImage')
          );

          return;
        }

        onImageLoad?.(img);

        setFilename(file.name);
        setImage(dataUrl);
      };

      img.src = dataUrl;
    };

    reader.readAsDataURL(file);
  };

  const validateCropDimensions = (width: number, height: number): boolean => {
    if (!minDimension) {
      return true;
    }

    if (!meetsMinDimension(width, height, minDimension)) {
      setError(t('dialogs.uploadImage.errors.tooSmall'));

      return false;
    }

    return true;
  };

  const handleCrop = () => {
    const cropper = cropperRef.current?.cropper;

    if (!cropper) {
      return;
    }

    setError(null);

    const naturalCrop = getNaturalCropDimensions(cropper);
    let finalWidth = Math.ceil(naturalCrop.width);
    let finalHeight = Math.ceil(naturalCrop.height);

    if (
      maxDimension &&
      (finalWidth > maxDimension.width || finalHeight > maxDimension.height)
    ) {
      const scaleFactor = Math.min(
        maxDimension.width / finalWidth,
        maxDimension.height / finalHeight
      );

      finalWidth = Math.round(finalWidth * scaleFactor);
      finalHeight = Math.round(finalHeight * scaleFactor);
    }

    if (!validateCropDimensions(finalWidth, finalHeight)) {
      return;
    }

    setIsProcessing(true);

    const croppedCanvas = cropper.getCroppedCanvas({
      width: finalWidth,
      height: finalHeight,
    });

    croppedCanvas.toBlob((blob) => {
      if (!blob) {
        setIsProcessing(false);
        setError(t('dialogs.uploadImage.errors.invalidImage'));

        return;
      }

      onCrop({
        filename: filename || 'untitled',
        blob: blob,
      });
    }, 'image/jpeg'); // TODO: implement other export extensions.
  };

  const onCropEnd = () => {
    const cropper = cropperRef.current?.cropper;

    if (!cropper || !minDimension) {
      return;
    }

    const naturalCrop = getNaturalCropDimensions(cropper);

    if (meetsMinDimension(naturalCrop.width, naturalCrop.height, minDimension)) {
      return;
    }

    const cropBoxData = cropper.getCropBoxData();
    const imageData = cropper.getImageData();
    const scaleX = imageData.naturalWidth / imageData.width;
    const scaleY = imageData.naturalHeight / imageData.height;
    const minWidth = minDimension.width;
    const minHeight = minDimension.height;

    const widthScale = minWidth / naturalCrop.width;
    const heightScale = minHeight / naturalCrop.height;
    const scale = Math.max(widthScale, heightScale);

    const newWidth = (naturalCrop.width * scale) / scaleX;
    const newHeight = (naturalCrop.height * scale) / scaleY;

    cropper.setCropBoxData({
      left: cropBoxData.left,
      top: cropBoxData.top,
      width: newWidth,
      height: newHeight,
    });
  };

  const buttons = [
    ...(isProcessing
      ? []
      : image
        ? [
            <Button key="reset" label={t('buttons.reset')} onClick={reset} />,
            <Button
              key="upload"
              label={t('dialogs.uploadImage.buttons.upload')}
              variant="primary"
              onClick={handleCrop}
            />,
          ]
        : [
            <Button
              key="close"
              label={t('buttons.close')}
              onClick={handleClose}
            />,
          ]),
  ];

  useEffect(() => {
    if (!show) {
      reset();
      setError(null);
    }
  }, [show]);

  useEffect(() => {
    const cropper = cropperRef.current?.cropper;

    if (!cropper || !cropperAspectRatio) {
      return;
    }

    cropper.setAspectRatio(cropperAspectRatio);
    syncMinCropSize();
  }, [cropperAspectRatio, syncMinCropSize]);

  return (
    <Dialog
      show={show}
      onClose={handleClose}
      title={title || t('dialogs.uploadImage.title')}
      subTitle={subTitle || t('dialogs.uploadImage.subTitle')}
      buttons={buttons}
    >
      {error && (
        <Alert
          severity="error"
          variant="filled"
          sx={{
            mb: '10px',
          }}
          onClose={() => setError(null)}
          content={error}
        />
      )}

      {!isProcessing && !image && (
        <Dropzone onSelect={onImageSelect}>{dropzoneContent}</Dropzone>
      )}
      {isProcessing && (
        <Box py={4} display="flex" justifyContent="center">
          <Loader />
        </Box>
      )}
      {!isProcessing && image && (
        <Fragment>
          <Cropper
            ref={cropperRef}
            src={image}
            style={{ height: 400, width: '100%' }}
            minCropBoxWidth={minCropSize?.width}
            minCropBoxHeight={minCropSize?.height}
            ready={syncMinCropSize}
            autoCrop={true}
            responsive={true}
            guides={true}
            background={true}
            viewMode={1} // make sure we can't select outside of the image.
            center={true}
            aspectRatio={cropperAspectRatio}
            cropend={minDimension ? onCropEnd : undefined}
            zoomable={false}
          />
          {cropperContent && (
            <Box
              sx={{
                mt: '10px',
              }}
            >
              {cropperContent}
            </Box>
          )}
        </Fragment>
      )}
    </Dialog>
  );
};
